import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { SearchableSelect } from '../../../../components/ui';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import useAnalyticsForms from '../../hooks/useAnalyticsForms';

const RECORDS_DATASET = 'records';

export default function ScopeFields({
  dataset = '',
  businessFunctions = [],
  accountId,
  campaignIds = [],
  leadsFormIds = [],
  onAccountChange,
  onCampaignChange,
  onLeadsFormChange,
  compact = false,
}) {
  const showRecordForms = dataset === RECORDS_DATASET && campaignIds.length > 0;
  const { forms, isLoading: formsLoading } = useAnalyticsForms(dataset, campaignIds);

  const campaigns = useMemo(() => {
    const bf = businessFunctions.find((item) => item.account_id === accountId);
    return bf?.campaigns || [];
  }, [businessFunctions, accountId]);

  const accountOptions = businessFunctions.map((item) => ({
    id: item.account_id,
    label: item.label,
  }));

  const campaignOptions = useMemo(
    () => campaigns.map((item) => ({
      id: item.campaign_id,
      label: item.label,
    })),
    [campaigns]
  );

  const selectedCampaigns = campaignOptions.filter((option) => campaignIds.includes(option.id));

  const serviceNameById = useMemo(() => {
    const names = {};
    campaigns.forEach((item) => {
      names[String(item.campaign_id)] = item.label;
    });
    return names;
  }, [campaigns]);

  const multipleServices = new Set(forms.map((form) => String(form.campaign_id || ''))).size > 1;

  const formOptions = useMemo(
    () => forms.map((item) => {
      const serviceName = serviceNameById[String(item.campaign_id || '')];
      const baseLabel = item.label;
      return {
        id: item.leads_form_id,
        label: multipleServices && serviceName ? `${baseLabel} · ${serviceName}` : baseLabel,
      };
    }),
    [forms, multipleServices, serviceNameById]
  );

  const selectedForms = formOptions.filter((option) => leadsFormIds.includes(option.id));

  return (
    <Stack spacing={2.5} maxWidth={compact ? '100%' : 520}>
      <SearchableSelect
        label={ANALYTICS_COPY.businessFunctionLabel}
        placeholder={ANALYTICS_COPY.businessFunctionPlaceholder}
        options={accountOptions}
        value={accountOptions.find((o) => o.id === accountId) || null}
        onChange={(opt) => onAccountChange(opt?.id || '')}
        getOptionLabel={(opt) => opt.label}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        required
      />
      <Box>
        <SearchableSelect
          multiple
          label={ANALYTICS_COPY.businessServiceLabel}
          placeholder={ANALYTICS_COPY.businessServicePlaceholder}
          options={campaignOptions}
          value={selectedCampaigns}
          onChange={(opts) => onCampaignChange((opts || []).map((opt) => opt.id))}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          disabled={!accountId}
          helperText={
            accountId && campaigns.length
              ? ANALYTICS_COPY.businessServiceHint
              : accountId
                ? ANALYTICS_COPY.noBusinessServices
                : undefined
          }
        />
      </Box>
      {showRecordForms ? (
        <Box>
          <SearchableSelect
            multiple
            label={ANALYTICS_COPY.recordFormLabel}
            placeholder={ANALYTICS_COPY.recordFormPlaceholder}
            options={formOptions}
            value={selectedForms}
            onChange={(opts) => onLeadsFormChange((opts || []).map((opt) => opt.id))}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={!campaignIds.length}
            loading={formsLoading}
            helperText={
              formsLoading
                ? ANALYTICS_COPY.recordFormsLoading
                : forms.length
                  ? ANALYTICS_COPY.recordFormHint
                  : ANALYTICS_COPY.noRecordForms
            }
          />
        </Box>
      ) : null}
    </Stack>
  );
}
